import './index.css';
import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import Layout from './components/Layout';
import LoadingSpinner from './components/LoadingSpinner';
import AssetsTab from './components/tabs/AssetsTab';
import SourcesTab from './components/tabs/SourcesTab';
import UserTab from './components/tabs/UserTab';
import WalletTab from './components/tabs/WalletTab';
import ConfigPage from './pages/ConfigPage';
import NewAssetsPage from './pages/NewAssetsPage';
import SplashPage from './pages/SplashPage';
import ErrorPage from './pages/ErrorPage';
import { RootPathes, TabPathes } from './types';
import { useTelegram } from './contexts/TelegramContext';
import { init } from '@telegram-apps/sdk';
import { BOT_CONFIG } from './api/client/config';
import { getCurrentUser } from './api/user';
import { useQueries, useQuery } from '@tanstack/react-query';
import { getAssets } from './api/assets';
import { getWalletInfo, getWalletTransactions } from './api/wallets';

const App = () => {
  const { tgApp, tgUser } = useTelegram();

  useEffect(() => {
    if (tgApp) {
      tgApp.ready();
      tgApp.expand();
      tgApp.setHeaderColor('#1a1a1a');
      tgApp.setBackgroundColor('#0a0a0a'); // TODO: take from vars
      init();
    }
  }, []);

  const { data, error, isPending } = useQuery({
    queryKey: ['user'],
    queryFn: getCurrentUser,
  });

  const queries = [
    { key: 'media-assets', fn: () => getAssets({ asset_type: 'media' }) },
    { key: 'wallet-info', fn: () => getWalletInfo() },
    { key: 'wallet-transactions', fn: () => getWalletTransactions() },
  ];

  const { pending } = useQueries({
    queries: queries.map(({ key, fn }) => ({
      queryKey: [key],
      queryFn: fn,
      enabled: !!data,
    })),
    combine: (results) => {
      return {
        data: results.map((result) => result.data),
        pending: results.some((result) => result.isPending),
        error: results.some((result) => result.error),
      };
    },
  });

  const botUrl = `https://t.me/${BOT_CONFIG.BOT_USERNAME}`;
  const appUrl = `${botUrl}/${BOT_CONFIG.APP_NAME}`;

  if (!tgUser) {
    return (
      <ErrorPage
        url={appUrl}
        errorMessage='Telegram user not found'
        status={400}
        buttonText='Open in Telegram'
      />
    );
  }

  if (!isPending && !data) {
    return (
      <ErrorPage
        url={appUrl}
        errorMessage='User not found'
        status={400}
        buttonText='Sign Up via bot'
      />
    );
  }

  if (isPending || pending || (!data && !error)) {
    return (
      <div className='flex min-h-screen items-center justify-center bg-[#0a0a0a]'>
        <LoadingSpinner />
      </div>
    );
  }

  if (!data && error) {
    return (
      <ErrorPage
        url={botUrl}
        errorMessage='User not found. Please register via bot.'
        status={404}
        buttonText='Open Telegram Bot'
      />
    );
  }

  return (
    <BrowserRouter>
      {data.firstLogin ? (
        <SplashPage />
      ) : (
        <Routes>
          <Route path={RootPathes.ROOT} element={<Layout user={data} />}>
            <Route
              index
              element={<Navigate to={TabPathes.DASHBOARD} replace />}
            />

            <Route path={TabPathes.DASHBOARD} element={<UserTab />} />

            <Route path={TabPathes.DATA} element={<SourcesTab user={data} />} />

            <Route path={TabPathes.ASSETS} element={<AssetsTab />} />

            <Route path={TabPathes.WALLET} element={<WalletTab />} />
          </Route>

          <Route path={RootPathes.CONFIG} element={<ConfigPage />} />

          <Route path={RootPathes.NEW_ASSET} element={<NewAssetsPage />} />
        </Routes>
      )}
    </BrowserRouter>
  );
};

export default App;
